import React, { useContext } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Row, Col, Card, Button, FormGroup, Label, Input, Table, UncontrolledTooltip, Alert } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { ButtonLoader } from '../../../components/Loader.js';
import Dropdown from '../../../components/Dropdown.js';
import { IoStar, IoInformationCircleOutline } from 'react-icons/io5';
import { MdOutlineDeleteForever } from 'react-icons/md';
import useSearchDrugQuote from '../../../services/drugQuotes/useSearchDrugQuote.js';
import useAddDrugQuote from '../../../services/drugQuotes/useAddDrugQuote.js';
import useSaveDrugQuote from '../../../services/drugQuotes/useSaveDrugQuote.js';
import useUpdateDrugQuote from '../../../services/drugQuotes/useUpdateDrugQuote.js';
import useRecalculateDrugQuote from '../../../services/drugQuotes/useRecalculateDrugQuote.js';
import { toast } from 'react-toastify';
import Pagination from '../../../components/Pagination.js';
import { SelectedFacilitiesContext } from '../../../contexts/SelectedFacilitiesContext.js';
import { UserContext } from '../../../contexts/UserContext.js';
import { hasRolePermission } from '../../../utility-services/rolePermissions.js';
import Swal from 'sweetalert2';

// Validation Schema
const DrugQuotesValidationSchema = Yup.object({
    quotesDescription: Yup.string().required('Quotes description is required'),
    type: Yup.string().required('Type is required'),
    drugName: Yup.string().required('Drug name is required'),
    quantity: Yup.number()
        .typeError('Quantity must be a number')
        .required('Quantity is required')
        .positive('Quantity must be a positive number'),
    daysSupply: Yup.number()
        .typeError('Days supply must be a number')
        .required('Days supply is required')
        .positive('Days supply must be a positive number'),
});

const typeOptions = [
    { value: 'r', label: 'Drug' },
    { value: 'c', label: 'Compound' },
    { value: 'i', label: 'IV' },
];

const formatPerDiemDisplay = (v) => {
    if (v === '' || v == null) return '-';
    const n = Number(v);
    if (!Number.isFinite(n)) return v;
    return String(Math.round(n * 100) / 100);
};

const getSearchResultPerDiem = (item) => {
    const v =
        item?.PerDiem ?? item?.perDiem ?? item?.PERDIEM ?? item?.per_diem ?? item?.PerDiemPrice ?? item?.perDiemPrice;
    return formatPerDiemDisplay(v);
};

/** int fields on AddDrugQuoteRequestDTO */
const toIntDto = (v) => {
    const n = Math.trunc(Number(v));
    return Number.isFinite(n) ? n : 0;
};

const toNumberDto = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/** Form may be cleared after add; fall back to line snapshot / payload when form value is empty. */
const resolveLineNumeric = (formValue, ...fallbacks) => {
    if (formValue !== '' && formValue != null) return formValue;
    for (const v of fallbacks) {
        if (v !== '' && v != null && v !== undefined) return v;
    }
    return '';
};

/**
 * Matches PocPostgresApi.Models.DTO.Utilities.AddDrugQuoteRequestDTO
 * (nested DrugQuoteResponseDto list kept minimal: NDC, DrugName.)
 */
const buildAddDrugQuoteRequestPayload = ({
    selectedDrug,
    selectedDrugItems,
    values,
    selectedDropdownValue,
    userId,
    price = 0,
    pricePerday = 0,
}) => {
    const ndc = selectedDrugItems[0]?.NDC ?? '';
    return {
        FacID: selectedDropdownValue || '',
        PharmID: '',
        NDC: ndc,
        Type: values.type || '',
        Qty: toNumberDto(values.quantity),
        Price: toIntDto(price),
        PricePerday: toIntDto(pricePerday),
        Daysupply: toIntDto(values.daysSupply),
        DrugName: values.drugName || '',
        Strength: (selectedDrug?.Strength ?? selectedDrug?.strength ?? '') || '',
        Drugtype: values.type || '',
        Drugdescription: values.quotesDescription?.trim() || '',
        DrugQuotes: selectedDrugItems,
        UserId: userId || '',
    };
};

function DrugQuotes() {
    const location = useLocation();
    const history = useHistory();
    const { facilities } = useContext(SelectedFacilitiesContext);
    const { user, roles } = useContext(UserContext);
    const canCreateDrugQuote = hasRolePermission(roles, 'canCreateDrugQuote');
    const { searchDrugQuote, isSearchDrugQuoteLoading } = useSearchDrugQuote();
    const { addDrugQuote, isAddDrugQuoteLoading } = useAddDrugQuote();
    const { saveDrugQuote, isSaveDrugQuoteLoading } = useSaveDrugQuote();
    const { updateDrugQuote, isUpdateDrugQuoteLoading } = useUpdateDrugQuote();
    const { recalculateDrugQuote, isRecalculateDrugQuoteLoading } = useRecalculateDrugQuote();
    const [searchResults, setSearchResults] = React.useState([]);
    const [searchPageNumber, setSearchPageNumber] = React.useState(1);
    const [searchPageSize, setSearchPageSize] = React.useState(10);
    const [searchTotalCount, setSearchTotalCount] = React.useState(0);
    const [searchTotalPages, setSearchTotalPages] = React.useState(1);
    const [selectedDrug, setSelectedDrug] = React.useState(null);
    const [quoteLineItems, setQuoteLineItems] = React.useState([]);
    const [editingQuoteId, setEditingQuoteId] = React.useState(null);
    const [editPendingLineItem, setEditPendingLineItem] = React.useState(null);
    const [selectedDropdownValue, setSelectedDropdownValue] = React.useState('');
    const [selectedFacilityPerDiem, setSelectedFacilityPerDiem] = React.useState(0);
    const [showPerDiemRestrictionModal, setShowPerDiemRestrictionModal] = React.useState(false);
    const [isSearchTableLoading, setIsSearchTableLoading] = React.useState(false);
    const initialValues = {
        quotesDescription: '',
        type: '',
        drugName: '',
        quantity: '',
        daysSupply: '',
    };
    const { values, errors, touched, handleChange, handleBlur, resetForm, setFieldValue, setFieldTouched } = useFormik({
        initialValues,
        validationSchema: DrugQuotesValidationSchema,
        validateOnMount: true,
        onSubmit: async () => {},
    });

    // "Add item to quote" should not depend on full form `isValid` (e.g. quotes description). Enable when line item + search context are ready.
    const qtyNum = values.quantity === '' || values.quantity == null ? NaN : Number(values.quantity);
    const daysNum = values.daysSupply === '' || values.daysSupply == null ? NaN : Number(values.daysSupply);
    const selectedDrugMinQtyDisp = (() => {
        const source =
            selectedDrug ||
            (editingQuoteId != null
                ? {
                      MinQtyDisp:
                          editPendingLineItem?.payload?.MinQtyDisp ??
                          editPendingLineItem?.payload?.minQtyDisp ??
                          editPendingLineItem?.minQtyDispSnapshot,
                  }
                : null);
        const minQty = source?.MinQtyDisp ?? source?.minQtyDisp;
        const minQtyNum = Number(minQty);
        return Number.isFinite(minQtyNum) && minQtyNum > 0 ? minQtyNum : NaN;
    })();
    const hasValidQtyAndDays = Number.isFinite(qtyNum) && qtyNum > 0 && Number.isFinite(daysNum) && daysNum > 0;
    const hasValidQuantityMultiple = Number.isFinite(selectedDrugMinQtyDisp)
        ? qtyNum >= selectedDrugMinQtyDisp && qtyNum % selectedDrugMinQtyDisp === 0
        : true;
    // Only show min-quantity errors after the user has interacted with the quantity field
    const quantityMinQtyError =
        Number.isFinite(selectedDrugMinQtyDisp) && Boolean(touched.quantity) && Number.isFinite(qtyNum) && qtyNum > 0
            ? qtyNum < selectedDrugMinQtyDisp
                ? `Minimum dispense quantity is ${selectedDrugMinQtyDisp}. Quantity can only be added in multiples of ${selectedDrugMinQtyDisp} (${selectedDrugMinQtyDisp}, ${selectedDrugMinQtyDisp * 2}, ${selectedDrugMinQtyDisp * 3}, ${selectedDrugMinQtyDisp * 4}, ...)`
                : qtyNum % selectedDrugMinQtyDisp !== 0
                  ? `Minimum dispense quantity is ${selectedDrugMinQtyDisp}. Quantity can only be added in multiples of ${selectedDrugMinQtyDisp} (${selectedDrugMinQtyDisp}, ${selectedDrugMinQtyDisp * 2}, ${selectedDrugMinQtyDisp * 3}, ${selectedDrugMinQtyDisp * 4}, ...)`
                  : ''
            : '';
    const minQtyHint =
        Number.isFinite(selectedDrugMinQtyDisp) && (values.quantity === '' || !Number.isFinite(qtyNum))
            ? `Minimum dispense quantity is ${selectedDrugMinQtyDisp}. Quantity can only be added in multiples of ${selectedDrugMinQtyDisp} (${selectedDrugMinQtyDisp}, ${selectedDrugMinQtyDisp * 2}, ${selectedDrugMinQtyDisp * 3}, ${selectedDrugMinQtyDisp * 4}, ...)`
            : '';
    const canAddLineItemToQuote =
        (editingQuoteId != null ? Boolean(editPendingLineItem) || Boolean(selectedDrug) : Boolean(selectedDrug)) &&
        hasValidQtyAndDays &&
        hasValidQuantityMultiple &&
        Boolean(values.type) &&
        Boolean(values.drugName) &&
        Boolean(selectedDropdownValue);
    const isAddItemDisabled = !canAddLineItemToQuote || isAddDrugQuoteLoading || !canCreateDrugQuote;
    const hasQuoteDescription = Boolean(values.quotesDescription?.trim());
    const canSaveDrugQuote = hasQuoteDescription && quoteLineItems.length > 0;
    const isSaveDrugQuoteDisabled = !canSaveDrugQuote || isSaveDrugQuoteLoading || isUpdateDrugQuoteLoading || !canCreateDrugQuote;
    const showSaveQuoteDescriptionTooltip = quoteLineItems.length > 0 && !hasQuoteDescription;
    const isSearchDisabled = !values.type || !values.drugName || isSearchDrugQuoteLoading;

    const facilityOptions = facilities
        .filter((fac) => fac?.facName != null && fac?.facID != null)
        .map((fac) => ({
            label: `(${fac.facID}) ${fac.facName}`,
            value: fac.facID,
            perDiem: Boolean(fac?.perDiem ?? fac?.PerDiem),
        }));

    /** Only when the org has a single facility — pick it for this page. Do not sync with navbar facility filter. */
    const soleFacilityId = facilityOptions.length === 1 ? String(facilityOptions[0].value ?? '') : '';

    React.useEffect(() => {
        if (soleFacilityId && !selectedDropdownValue) {
            setSelectedDropdownValue(soleFacilityId);

            const selectedFacility = facilityOptions.find((f) => String(f.value) === String(soleFacilityId));

            setSelectedFacilityPerDiem(selectedFacility?.perDiem ?? 0);
        }
    }, [soleFacilityId, selectedDropdownValue, facilityOptions]);

    React.useEffect(() => {
        const editState = location.state;
        if (!editState?.editQuoteId) return;
        const { editQuoteId, form, quoteLineItem, items, facId } = editState;
        const editFacId =
            facId ??
            quoteLineItem?.payload?.FacID ??
            quoteLineItem?.payload?.facID ??
            items?.[0]?.facID ??
            items?.[0]?.FacID ??
            '';
        setEditingQuoteId(editQuoteId);
        setFieldValue('quotesDescription', form?.quotesDescription ?? '');
        const editTypeRaw = form?.type ?? '';
        if (editTypeRaw) {
            const normalized = String(editTypeRaw).trim().toLowerCase();
            const match = typeOptions.find(
                (opt) =>
                    String(opt.value).toLowerCase() === normalized || String(opt.label).toLowerCase() === normalized
            );
            setFieldValue('type', match ? String(match.value) : String(editTypeRaw));
        } else {
            setFieldValue('type', '');
        }
        setFieldValue('drugName', '');
        setFieldValue('quantity', form?.quantity ?? '');
        setFieldValue('daysSupply', form?.daysSupply ?? '');

        if (editFacId) {
            const match = facilityOptions.find(
                (f) => String(f.value).toLowerCase() === String(editFacId).toLowerCase()
            );

            setSelectedDropdownValue(match ? String(match.value) : String(editFacId));

            setSelectedFacilityPerDiem(Boolean(match?.perDiem));
        }
        if (Array.isArray(items) && items.length > 0) {
            setEditPendingLineItem(null);
            setQuoteLineItems(
                items.map((drug, index) => createQuoteLineItemFromSavedDrug(drug, form, editFacId, index))
            );
        } else if (quoteLineItem) {
            setEditPendingLineItem(quoteLineItem);
            setQuoteLineItems([]);
        }
        history.replace({ pathname: location.pathname, state: {} });
    }, [location.state, location.pathname, history, setFieldValue, facilityOptions]);

    const handleDrugSearch = (page = 1) => {
        if (!selectedDropdownValue) {
            toast.error('You must select a facility from facility dropdown');
            return;
        }

        const facId = selectedDropdownValue || '';
        const searchTypeIsDrug = values.type === 'r' || values.type === 'c';

        const requestBody = {
            drugName: values.drugName || '',
            type: values.type || '',
            facId,
            UserId: user?.userId || '',
            pageNumber: page,
            pageSize: searchPageSize,
            perDiem: selectedFacilityPerDiem,
        };
        setIsSearchTableLoading(true);
        searchDrugQuote(requestBody, {
            onSuccess: (data) => {
                setIsSearchTableLoading(false);

                const items =
                    data?.items ??
                    data?.result ??
                    data?.data?.items ??
                    data?.data?.result ??
                    (Array.isArray(data) ? data : []);

                const totalCount = Number(
                    data?.totalCount ?? data?.total ?? data?.data?.totalCount ?? items.length ?? 0
                );

                const totalPages = Number(
                    data?.totalPages ?? Math.max(1, Math.ceil(totalCount / (searchPageSize || 10)))
                );

                setSearchResults(Array.isArray(items) ? items : []);
                if ((Array.isArray(items) ? items : []).length === 0) {
                    Swal.fire({
                        title: '',
                        text: 'Please contact pharmacy for price quote.',
                        icon: 'info',
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#5369f8',
                    });
                }
                setSearchTotalCount(totalCount);
                setSearchTotalPages(totalPages);
                setSearchPageNumber(page);

                setSelectedDrug(null);
            },
        });
    };
    const isPerDiemRestricted = selectedFacilityPerDiem && ['i'].includes(values.type);
    const getDrugNdc = (drug) => drug?.NDC ?? drug?.ndc ?? '';

    const getQuoteId = (item) => {
        if (!item || typeof item !== 'object') return null;
        return (
            item.drugQuoteItemId ??
            item.drugQuoteId ??
            item.DrugQuoteId ??
            item.drugQuoteID ??
            item.DrugQuoteID ??
            item.quoteId ??
            item.QuoteId ??
            item.id ??
            item.Id ??
            item.ID ??
            null
        );
    };

    const createQuoteLineItemFromSavedDrug = (drug, form, facId, index) => {
        const quoteId = getQuoteId(drug);
        const drugName = drug?.drugName ?? drug?.DrugName ?? '';
        const quantity = drug?.quantity ?? drug?.qty ?? drug?.Qty ?? '';
        const daysSupply = drug?.daysSupply ?? drug?.Daysupply ?? drug?.daysupply ?? '';
        const ndc = drug?.ndc ?? drug?.NDC ?? '';
        const typeValue = drug?.type ?? drug?.Type ?? drug?.drugtype ?? drug?.Drugtype ?? '';
        const strength = drug?.strength ?? drug?.Strength ?? '';
        const cost = drug?.cost ?? drug?.Cost ?? drug?.price ?? drug?.Price ?? '';
        const pricePerDay = drug?.pricePerDay ?? drug?.PricePerDay ?? drug?.pricePerday ?? drug?.PricePerday ?? '';
        const isIncluded = drug?.isIncluded ?? drug?.IsIncluded ?? false;

        return {
            id: `${quoteId ?? 'saved'}-${index}-${Math.random().toString(36).slice(2, 9)}`,
            payload: {
                Type: typeValue,
                NDC: ndc,
                Qty: toNumberDto(quantity),
                Daysupply: toIntDto(daysSupply),
                DrugName: drugName,
                searchDrug: form?.searchDrug ?? '',
                FacID: facId || drug?.facID || drug?.FacID || '',
                facID: facId || drug?.facID || drug?.FacID || '',
            },
            response: {},
            finalTotalPriceSnapshot: cost,
            pricePerDaySnapshot: pricePerDay,
            isIncludedSnapshot: Boolean(isIncluded),
            strengthSnapshot: strength,
            searchDrug: form?.searchDrug ?? '',
            quantitySnapshot: quantity,
            daysSupplySnapshot: daysSupply,
            drugNameSnapshot: drugName,
            dosageFormSnapshot: drug?.dosageForm ?? drug?.DosageForm ?? drug?.Form ?? '',
            minQtyDispSnapshot: drug?.MinQtyDisp ?? drug?.minQtyDisp ?? '',
        };
    };

    const isDrugSelected = (drug) => {
        if (!selectedDrug) return false;
        return getDrugNdc(selectedDrug) === getDrugNdc(drug);
    };

    const selectDrugRow = (drug) => {
        const name = String(drug?.DrugName ?? drug?.drugName ?? '');
        const strength = String(drug?.Strength ?? drug?.strength ?? '');
        const indicator = '*CSP';
        if (name.toUpperCase().includes(indicator) || strength.toUpperCase().includes(indicator)) {
            Swal.fire({
                title: '',
                text: 'This IV is mixed and non-returnable.',
                icon: 'warning',
                confirmButtonText: 'Continue',
                confirmButtonColor: '#5369f8',
            });
        }
        setSelectedDrug((prev) => {
            const ndc = getDrugNdc(drug);
            if (prev && getDrugNdc(prev) === ndc) return null;
            // No toast for regular selection as requested
            return drug;
        });
    };
    const normalizeAddQuoteResponse = (raw) => {
        if (raw == null || typeof raw !== 'object') return {};
        const nested = raw.data;
        if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
            return { ...raw, ...nested };
        }
        return { ...raw };
    };

    /** API may nest `finalTotalPrice` under `data`, `result`, etc. */
    const extractFinalTotalPrice = (raw) => {
        const root = normalizeAddQuoteResponse(raw);
        const visit = (obj, depth) => {
            if (obj == null || typeof obj !== 'object' || depth > 8) return undefined;
            const v = obj.finalTotalPrice ?? obj.FinalTotalPrice ?? obj.finaltotalprice ?? obj.FINALTOTALPRICE;
            if (v !== undefined && v !== null && v !== '') return v;
            for (const val of Object.values(obj)) {
                if (val && typeof val === 'object' && !Array.isArray(val)) {
                    const found = visit(val, depth + 1);
                    if (found !== undefined) return found;
                }
            }
            return undefined;
        };
        return visit(root, 0);
    };

    /** API may nest `pricePerDay` under `data`, `result`, etc. */
    const extractPricePerDay = (raw) => {
        const root = normalizeAddQuoteResponse(raw);
        const visit = (obj, depth) => {
            if (obj == null || typeof obj !== 'object' || depth > 8) return undefined;
            const v =
                obj.pricePerDay ??
                obj.PricePerDay ??
                obj.pricePerday ??
                obj.PricePerday ??
                obj.priceDay ??
                obj.PriceDay ??
                obj.dailyPrice ??
                obj.DailyPrice;
            if (v !== undefined && v !== null && v !== '') return v;
            for (const val of Object.values(obj)) {
                if (val && typeof val === 'object' && !Array.isArray(val)) {
                    const found = visit(val, depth + 1);
                    if (found !== undefined) return found;
                }
            }
            return undefined;
        };
        return visit(root, 0);
    };

    const extractIsIncluded = (raw) => {
        const root = normalizeAddQuoteResponse(raw);
        const visit = (obj, depth) => {
            if (obj == null || typeof obj !== 'object' || depth > 8) return undefined;
            const v = obj.isIncluded ?? obj.IsIncluded ?? obj.isincluded ?? obj.ISINCLUDED;
            if (v !== undefined && v !== null) return v;
            for (const val of Object.values(obj)) {
                if (val && typeof val === 'object' && !Array.isArray(val)) {
                    const found = visit(val, depth + 1);
                    if (found !== undefined) return found;
                }
            }
            return undefined;
        };
        return visit(root, 0);
    };

    const formatDecimalDisplay = (v) => {
        if (v === '' || v == null) return '-';
        const n = Number(v);
        if (!Number.isFinite(n)) return v;
        return String(Math.round(n * 100) / 100);
    };

    const removeQuoteLineItem = (id) => {
        setQuoteLineItems((prev) => prev.filter((row) => row.id !== id));
    };

    const resolveCatalogDrugName = (row, drugFromTable) => {
        if (drugFromTable) {
            return String(drugFromTable.DrugName ?? drugFromTable.drugName ?? '');
        }
        const p = row?.payload || {};
        const nested0 = Array.isArray(p.DrugQuotes) && p.DrugQuotes[0] ? p.DrugQuotes[0] : {};
        return String(row?.drugNameSnapshot ?? p.DrugName ?? p.drugName ?? nested0.DrugName ?? '');
    };

    const buildQuoteLineFields = (row) => {
        const p = row.payload || {};
        const nested0 = Array.isArray(p.DrugQuotes) && p.DrugQuotes[0] ? p.DrugQuotes[0] : {};
        const r = row.response || {};
        const total =
            row.finalTotalPriceSnapshot ??
            extractFinalTotalPrice(r) ??
            extractFinalTotalPrice(row.response) ??
            extractFinalTotalPrice(p);
        const ppdRaw =
            row.pricePerDaySnapshot ??
            extractPricePerDay(r) ??
            extractPricePerDay(row.response) ??
            extractPricePerDay(p);
        const costNum = Number(total);
        const cost = Number.isFinite(costNum) ? costNum : 0;
        const ppd = Number(ppdRaw);
        const pricePerDay = Number.isFinite(ppd) ? ppd : 0;
        const catalogDrugName =
            row.drugNameSnapshot || p.DrugName || p.drugName || nested0.DrugName || nested0.drugName || '';
        const catalogNdc = String(p.NDC ?? p.ndc ?? nested0.NDC ?? nested0.ndc ?? '');
        const catalogStrength =
            row.strengthSnapshot || String(p.Strength ?? p.strength ?? nested0.Strength ?? nested0.strength ?? '');
        const resolvedQuantity = row.quantitySnapshot ?? p.Qty ?? p.qty ?? nested0.Qty ?? nested0.qty ?? '';
        const resolvedDaysSupply =
            row.daysSupplySnapshot ??
            p.Daysupply ??
            p.daysSupply ??
            p.daysupply ??
            nested0.Daysupply ??
            nested0.daysSupply ??
            nested0.daysupply ??
            '';

        return {
            drugName: catalogDrugName,
            searchDrug: String(row.searchDrug || p.searchDrug || p.SearchDrug || ''),
            strength: catalogStrength,
            type: String(p.Type || p.type || p.Drugtype || p.drugtype || ''),
            ndc: catalogNdc,
            quantity: toNumberDto(resolvedQuantity),
            daysSupply: toIntDto(resolvedDaysSupply),
            cost,
            pricePerDay,
            isIncluded: Boolean(row.isIncludedSnapshot),
            perDiem: Boolean(selectedFacilityPerDiem),
            CreatedById: user?.userId || '',
            UserId: user?.userId || '',
            facID: String(p.FacID || p.facID || row.facID || row.facid || ''),
        };
    };

    /** POST /drugsearch/savequote — only these fields (per line; always submit an array of line objects). */
    const buildSaveQuotePayload = () => {
        if (quoteLineItems.length === 0) return null;
        const quotesDescription = values.quotesDescription?.trim() || '';
        return quoteLineItems.map((row) => ({
            ...buildQuoteLineFields(row),
            quotesDescription,
        }));
    };

    /** PUT /drugsearch/updatequote — same fields as save + quote id. */
    const buildUpdateQuotePayload = () => {
        if (editingQuoteId == null || quoteLineItems.length === 0) return null;
        const quotesDescription = values.quotesDescription?.trim() || '';
        return {
            id: editingQuoteId,
            drugQuoteId: editingQuoteId,
            quotesDescription,
            facID: selectedDropdownValue || '',
            drugName: values.drugName || '',
            type: values.type || '',
            UpdatedById: user?.userId || '',
            items: quoteLineItems.map((row) => ({
                ...buildQuoteLineFields(row),
                quotesDescription,
            })),
        };
    };

    const clearPageAfterSaveOrUpdate = () => {
        resetForm({ values: initialValues, touched: {}, errors: {} });
        setQuoteLineItems([]);
        setEditingQuoteId(null);
        setEditPendingLineItem(null);
        setSearchResults([]);
        // setShowSearchPerDiemColumn(false);
        setSelectedDrug(null);
        setSelectedDropdownValue(soleFacilityId || '');
    };

    const extractRecalculatedItems = (data) => {
        const root = data?.data ?? data?.result ?? data;
        if (Array.isArray(root)) return root;
        if (root == null || typeof root !== 'object') return null;
        const items =
            root.items ?? root.Items ?? root.drugQuotes ?? root.DrugQuotes ?? root.data?.items ?? root.data?.Items;
        return Array.isArray(items) ? items : null;
    };

    const applyRecalculatedQuoteToLineItems = (data) => {
        const root = data?.data ?? data?.result ?? data;
        const items = extractRecalculatedItems(data);
        if (Array.isArray(items) && items.length > 0) {
            setQuoteLineItems(
                items.map((drug, index) =>
                    createQuoteLineItemFromSavedDrug(
                        drug,
                        { searchDrug: values.drugName },
                        selectedDropdownValue,
                        index
                    )
                )
            );
        }
        const description =
            root?.quotesDescription ?? root?.QuotesDescription ?? root?.drugdescription ?? root?.Drugdescription;
        if (description) {
            setFieldValue('quotesDescription', String(description));
        }
    };

    const handleRecalculateQuote = () => {
        if (editingQuoteId == null) return;
        recalculateDrugQuote(editingQuoteId, {
            onSuccess: (data) => {
                applyRecalculatedQuoteToLineItems(data);
            },
        });
    };

    const handleSaveDrugQuote = () => {
        if (!canSaveDrugQuote || !canCreateDrugQuote) return;

        if (editingQuoteId != null) {
            const body = buildUpdateQuotePayload();
            if (!body) return;
            updateDrugQuote(body, {
                onSuccess: () => {
                    clearPageAfterSaveOrUpdate();
                },
            });
            return;
        }

        const body = buildSaveQuotePayload();
        if (!body) return;
        saveDrugQuote(body, {
            onSuccess: () => {
                clearPageAfterSaveOrUpdate();
            },
        });
    };

    const handleAddItemToQuote = () => {
        if (!canCreateDrugQuote) return;
        if (
            Number.isFinite(selectedDrugMinQtyDisp) &&
            (!Number.isFinite(qtyNum) || qtyNum <= 0 || !hasValidQuantityMultiple)
        ) {
            const defaultMsg = `Minimum dispense quantity is ${selectedDrugMinQtyDisp}. Quantity can only be added in multiples of ${selectedDrugMinQtyDisp} (${selectedDrugMinQtyDisp}, ${selectedDrugMinQtyDisp * 2}, ${selectedDrugMinQtyDisp * 3}, ${selectedDrugMinQtyDisp * 4}, ...)`;
            toast.error(quantityMinQtyError || defaultMsg);
            return;
        }

        if (editingQuoteId != null && editPendingLineItem) {
            const basePayload = editPendingLineItem.payload || {};
            const catalogDrugName = resolveCatalogDrugName(editPendingLineItem, selectedDrug);
            const catalogNdc = selectedDrug ? getDrugNdc(selectedDrug) : (basePayload.NDC ?? '');
            const catalogStrength = selectedDrug
                ? String(selectedDrug.Strength ?? selectedDrug.strength ?? '')
                : (editPendingLineItem.strengthSnapshot ?? '');
            setQuoteLineItems([
                {
                    ...editPendingLineItem,
                    payload: {
                        ...basePayload,
                        Type: values.type || basePayload.Type || '',
                        Qty: toNumberDto(values.quantity),
                        Daysupply: toIntDto(values.daysSupply),
                        FacID: selectedDropdownValue || basePayload.FacID || basePayload.facID || '',
                        facID: selectedDropdownValue || basePayload.FacID || basePayload.facID || '',
                        ...(catalogNdc ? { NDC: catalogNdc } : {}),
                        ...(catalogDrugName ? { DrugName: catalogDrugName, drugName: catalogDrugName } : {}),
                    },
                    searchDrug: values.drugName?.trim() || editPendingLineItem.searchDrug || '',
                    drugNameSnapshot: catalogDrugName || editPendingLineItem.drugNameSnapshot,
                    strengthSnapshot: catalogStrength,
                    quantitySnapshot: values.quantity,
                    daysSupplySnapshot: values.daysSupply,
                },
            ]);
            return;
        }

        const selectedDrugItems = selectedDrug
            ? [
                  {
                      NDC: getDrugNdc(selectedDrug),
                      DrugName: selectedDrug?.DrugName ?? selectedDrug?.drugName ?? '',
                  },
              ]
            : [];

        const payload = buildAddDrugQuoteRequestPayload({
            selectedDrug,
            selectedDrugItems,
            values,
            selectedDropdownValue,
            userId: user?.userId,
            price: 0,
            pricePerday: 0,
        });

        addDrugQuote(payload, {
            onSuccess: (responseData) => {
                const rNorm = normalizeAddQuoteResponse(responseData);
                const finalTotal = extractFinalTotalPrice(responseData);
                const pricePerDay = extractPricePerDay(responseData);
                const isIncluded = extractIsIncluded(responseData);
                const rowId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
                setQuoteLineItems((prev) => [
                    ...prev,
                    {
                        id: rowId,
                        payload: { ...payload },
                        response: rNorm,
                        finalTotalPriceSnapshot: finalTotal,
                        pricePerDaySnapshot: pricePerDay,
                        isIncludedSnapshot: Boolean(isIncluded),
                        strengthSnapshot: selectedDrug?.Strength ?? selectedDrug?.strength ?? '',
                        searchDrug: values.drugName || '',
                        quantitySnapshot: values.quantity,
                        daysSupplySnapshot: values.daysSupply,
                        drugNameSnapshot: selectedDrug?.DrugName ?? selectedDrug?.drugName ?? payload.DrugName ?? '',
                        dosageFormSnapshot:
                            selectedDrug?.DosageForm ?? selectedDrug?.dosageForm ?? selectedDrug?.Form ?? '',
                        minQtyDispSnapshot: selectedDrug?.MinQtyDisp ?? selectedDrug?.minQtyDisp ?? '',
                    },
                ]);
                setSelectedDrug(null);
                setFieldValue('quantity', '', true);
                setFieldValue('daysSupply', '', true);
                setFieldTouched('quantity', false, true);
                setFieldTouched('daysSupply', false, true);
            },
        });
    };

    const getTypeLabel = (value) => {
        return typeOptions.find((item) => item.value === value)?.label || value || '-';
    };

    return (
        <div className="page-content">
            <div className="d-flex align-items-center justify-content-between">
                <h2 className="mb-2">Drug Quotes</h2>
                <Button
                    tag={Link}
                    to="/utilities/drug-quotes/saved"
                    style={{
                        cursor: 'pointer',
                        borderRadius: '5px',
                        padding: '8px 15px',
                        border: 'none',
                        outline: 'none',
                        backgroundColor: '#6952AD',
                        color: 'white',
                    }}>
                    Access Saved Quotes
                </Button>
            </div>
            <Alert
                color="info"
                className="d-flex align-items-center mb-3"
                style={{
                    borderRadius: '8px',
                    border: '1px solid #b8daff',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                }}>
                <IoInformationCircleOutline style={{ fontSize: '20px', flexShrink: 0, marginRight: '10px' }} />
                <span>
                    Any questions, please contact the price quotes team at:{' '}
                    <a href="mailto:pricequotes@srxltc.com" style={{ fontWeight: '600' }}>
                        pricequotes@srxltc.com
                    </a>
                </span>
            </Alert>
            <Card>
                <FormGroup className="p-2">
                    <div className="d-flex">
                        <Label for="drugName" className="mr-2 ">
                            Quotes Description
                        </Label>
                        <IoStar style={{ color: 'red', fontSize: '6px' }} />
                    </div>
                    <Input
                        type="test"
                        name="quotesDescription"
                        id="quotesDescription"
                        placeholder="Enter quotes description"
                        value={values.quotesDescription}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={errors.quotesDescription && touched.quotesDescription}
                    />
                    {errors.quotesDescription && touched.quotesDescription && (
                        <div className="invalid-feedback d-block">{errors.quotesDescription}</div>
                    )}
                </FormGroup>
            </Card>
            <Card>
                <Row className="align-items-start">
                    <Col xs={12} sm={6} md={3}>
                        <FormGroup className="p-2 mb-0">
                            <Label for="type">Type</Label>
                            <Dropdown
                                label="Type"
                                options={typeOptions}
                                placeholder="Select type..."
                                selectedDropdownValue={values.type}
                                setSelectedDropdownValue={(value) =>
                                    handleChange({
                                        target: {
                                            name: 'type',
                                            value: value,
                                        },
                                    })
                                }
                                invalid={errors.type && touched.type}
                                showEmptyOption={false}
                                emptyOptionLabel="None"
                            />
                            {errors.type && touched.type && (
                                <div className="invalid-feedback d-block mt-1">{errors.type}</div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                        <FormGroup className="p-2 mb-0">
                            <Label for="drugName">Drug Name</Label>
                            <Input
                                type="text"
                                name="drugName"
                                id="drugName"
                                placeholder="Enter drug name"
                                value={values.drugName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={errors.drugName && touched.drugName}
                            />
                            {errors.drugName && touched.drugName && (
                                <div className="invalid-feedback d-block mt-1">{errors.drugName}</div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col xs={12} sm={8} md={4}>
                        {facilityOptions.length > 1 ? (
                            <FormGroup className="p-2 mb-0">
                                <Label for="facility">Facility</Label>
                                <span id="facility-select-wrapper" style={{ display: 'block' }}>
                                    <Dropdown
                                        label="Facility"
                                        options={facilityOptions}
                                        placeholder="Choose a facility"
                                        selectedDropdownValue={selectedDropdownValue}
                                        // setSelectedDropdownValue={setSelectedDropdownValue}
                                        setSelectedDropdownValue={(value) => {
                                            setSelectedDropdownValue(value);

                                            const selectedFacility = facilityOptions.find(
                                                (f) => String(f.value) === String(value)
                                            );

                                            setSelectedFacilityPerDiem(selectedFacility?.perDiem ?? false);
                                        }}
                                        showEmptyOption={false}
                                        emptyOptionLabel="Choose a facility"
                                        disabled={quoteLineItems.length > 0 || editingQuoteId != null}
                                    />
                                </span>
                                {(quoteLineItems.length > 0 || editingQuoteId != null) && (
                                    <UncontrolledTooltip placement="top" target="facility-select-wrapper">
                                        The facility cannot be changed for this quote once items have been added.
                                    </UncontrolledTooltip>
                                )}
                            </FormGroup>
                        ) : facilityOptions.length === 1 && selectedDropdownValue ? (
                            <FormGroup className="p-2 mb-0">
                                <Label for="facility">Facility</Label>
                                <div
                                    className="border"
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        fontWeight: '500',
                                        color: '#333',
                                        fontSize: '14px',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>
                                    {facilityOptions[0]?.label}
                                </div>
                            </FormGroup>
                        ) : null}
                    </Col>
                    <Col xs={12} sm={4} md={2}>
                        <FormGroup className="p-2 mb-0">
                            <Label aria-hidden="true" style={{ visibility: 'hidden' }}>
                                &nbsp;
                            </Label>
                            <Button
                                block
                                onClick={() => handleDrugSearch(1)}
                                disabled={isSearchDisabled}
                                style={{
                                    cursor: isSearchDisabled ? 'default' : 'pointer',
                                    borderRadius: '5px',
                                    padding: '8px 15px',
                                    border: 'none',
                                    outline: 'none',
                                    backgroundColor: 'blue',
                                    color: 'white',
                                    opacity: isSearchDisabled ? 0.6 : 1,
                                }}>
                                {isSearchDrugQuoteLoading ? <ButtonLoader /> : 'Search'}
                            </Button>
                        </FormGroup>
                    </Col>
                </Row>
            </Card>

            <Card className="p-2">
                <Row>
                    <Col md={12}>
                        <Table className="table-bordered">
                            <thead className="thead-light">
                                <tr>
                                    <th style={{ width: '40px', textAlign: 'center' }} aria-label="Select drug" />
                                    <th>Drug Name</th>
                                    <th>NDC</th>
                                    <th>Form</th>
                                    <th>Strength</th>
                                    <th>Min Dispense Quantity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isSearchTableLoading ? (
                                    <>
                                        <tr col>
                                            <td colSpan="7" className="text-center p-4">
                                                <h5>Loading...</h5>
                                                <div className="bouncing-loader">
                                                    <div></div>
                                                    <div></div>
                                                    <div></div>
                                                </div>
                                            </td>
                                        </tr>
                                    </>
                                ) : searchResults.length > 0 ? (
                                    searchResults.map((item, index) => {
                                        const ndc = getDrugNdc(item);
                                        const drugName = item?.DrugName ?? item?.drugName ?? '-';
                                        const checked = isDrugSelected(item);

                                        return (
                                            <tr key={`${ndc || drugName}-${index}`}>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => selectDrugRow(item)}
                                                    />
                                                </td>
                                                <td>{drugName}</td>
                                                <td>{ndc || '-'}</td>
                                                <td>{item?.DosageForm || '-'}</td>
                                                <td>{item?.Strength ? formatDecimalDisplay(item.Strength) : '-'}</td>
                                                <td>{item?.MinQtyDisp || '-'}</td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="text-center p-4">
                                            Search results will appear here
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>

                        <div className="mt-2">
                            <Pagination
                                pageNumber={searchPageNumber}
                                pageSize={searchPageSize}
                                totalCount={searchTotalCount}
                                totalPages={searchTotalPages}
                                onPageChange={(p) => handleDrugSearch(p)}
                            />
                        </div>
                    </Col>
                </Row>

                <Row className="align-items-start">
                    <Col md={4}>
                        <FormGroup className="p-2 d-flex flex-column">
                            <div className="d-flex">
                                <Label for="quantity">Quantity</Label>
                                <IoStar style={{ color: 'red', fontSize: '6px' }} />
                            </div>

                            <Input
                                type="text"
                                name="quantity"
                                id="quantity"
                                placeholder="Enter quantity"
                                value={values.quantity}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={(errors.quantity && touched.quantity) || Boolean(quantityMinQtyError)}
                            />

                            <div style={{ minHeight: '20px', marginTop: '4px' }}>
                                {(errors.quantity && touched.quantity) || quantityMinQtyError ? (
                                    <div className="invalid-feedback d-block">
                                        {quantityMinQtyError || errors.quantity}
                                    </div>
                                ) : minQtyHint ? (
                                    <small className="text-muted">{minQtyHint}</small>
                                ) : null}
                            </div>
                        </FormGroup>
                    </Col>

                    <Col md={4}>
                        <FormGroup className="p-2 d-flex flex-column">
                            <div className="d-flex">
                                <Label for="daysSupply">Days Supply</Label>
                                <IoStar style={{ color: 'red', fontSize: '6px' }} />
                            </div>

                            <Input
                                type="text"
                                name="daysSupply"
                                id="daysSupply"
                                placeholder="Enter days supply"
                                value={values.daysSupply}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={errors.daysSupply && touched.daysSupply}
                            />

                            <div style={{ minHeight: '20px', marginTop: '4px' }}>
                                {errors.daysSupply && touched.daysSupply && (
                                    <div className="invalid-feedback d-block">{errors.daysSupply}</div>
                                )}
                            </div>
                        </FormGroup>
                    </Col>

                    <Col
                        md={4}
                        style={{
                            paddingTop: '28px',
                        }}
                        className="d-flex align-items-start ">
                        <Button
                            onClick={handleAddItemToQuote}
                            disabled={isAddItemDisabled}
                            style={{
                                cursor: isAddItemDisabled ? 'default' : 'pointer',
                                borderRadius: '5px',
                                padding: '10px 18px',
                                border: 'none',
                                outline: 'none',
                                backgroundColor: '#00A300',
                                color: 'white',
                                opacity: isAddItemDisabled ? 0.6 : 1,
                                height: '38px',
                                marginTop: '12px',
                            }}>
                            {isAddDrugQuoteLoading ? <ButtonLoader /> : 'Add item to quote'}
                        </Button>
                    </Col>
                </Row>
            </Card>
            <Card className="p-2">
                {Boolean(selectedFacilityPerDiem) && (
                    <div className="p-2" style={{ fontWeight: '600' }}>
                        Per Diem Facility
                    </div>
                )}
                <Row>
                    <Col md={12}>
                        <div
                            style={{
                                maxHeight: '300px',
                                overflowY: 'auto',
                                overflowX: 'auto',
                                width: '100%',
                            }}>
                            <Table
                                className="table-bordered"
                                style={{
                                    marginBottom: '0',
                                    minWidth: '1200px',
                                    whiteSpace: 'nowrap',
                                }}>
                                <thead className="thead-light" style={{ position: 'sticky', top: '0', zIndex: '10' }}>
                                    <tr>
                                        <th>Drug Name</th>
                                        <th>Strength</th>
                                        <th>Type</th>
                                        <th>NDC</th>
                                        <th>Quantity</th>
                                        <th>Days Supply</th>
                                        <th>{selectedFacilityPerDiem ? 'Excluded Cost' : 'Cost'}</th>
                                        <th>Price/Day</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {quoteLineItems.length > 0 ? (
                                        quoteLineItems.map((row) => {
                                            const p = row.payload || {};
                                            const r = row.response || {};
                                            const finalTotal =
                                                row.finalTotalPriceSnapshot ??
                                                extractFinalTotalPrice(r) ??
                                                extractFinalTotalPrice(row.response);
                                            const lineCost = finalTotal;
                                            const pricePerDayField =
                                                row.pricePerDaySnapshot ??
                                                extractPricePerDay(r) ??
                                                extractPricePerDay(row.response);
                                            const isIncludedField = Boolean(
                                                row.isIncludedSnapshot ??
                                                extractIsIncluded(r) ??
                                                extractIsIncluded(row.response)
                                            );
                                            return (
                                                <tr key={row.id}>
                                                    <td>{row.drugNameSnapshot || p.DrugName || '-'}</td>
                                                    <td>
                                                        {row.strengthSnapshot
                                                            ? formatDecimalDisplay(row.strengthSnapshot)
                                                            : '-'}
                                                    </td>
                                                    <td>{getTypeLabel(p.Type)}</td>{' '}
                                                    <td>{p.NDC || p.DrugQuotes?.[0]?.NDC || '-'}</td>
                                                    <td>{p.Qty ?? '-'}</td>
                                                    <td>{p.Daysupply ?? '-'}</td>
                                                    <td>${formatDecimalDisplay(lineCost)}</td>
                                                    <td>
                                                        {isIncludedField
                                                            ? 'included in daily rate'
                                                            : `$${formatDecimalDisplay(pricePerDayField)}`}
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            aria-label="Remove line"
                                                            onClick={() => removeQuoteLineItem(row.id)}
                                                            style={{
                                                                cursor: 'pointer',
                                                                outline: 'none',
                                                                border: 'none',
                                                                backgroundColor: '#FFB0C4',
                                                                borderRadius: '50%',
                                                                padding: '5px',
                                                                width: '30px',
                                                                height: '30px',
                                                            }}>
                                                            <MdOutlineDeleteForever
                                                                style={{ fontSize: '20px' }}
                                                                color="red"
                                                            />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={selectedFacilityPerDiem ? 9 : 8} className="text-center p-4">
                                                Add items to see them in this quote summary
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Col>
                </Row>
            </Card>
            <Col className="d-flex justify-content-end" md={12}>
                <div className="d-flex justify-content-end">
                    <span id="save-drug-quote-btn" style={{ display: 'inline-block' }}>
                        <Button
                            style={{
                                cursor: isSaveDrugQuoteDisabled ? 'default' : 'pointer',
                                borderRadius: '5px',
                                padding: '10px 20px',
                                border: 'none',
                                outline: 'none',
                                backgroundColor: 'blue',
                                color: 'white',
                                opacity: isSaveDrugQuoteDisabled ? 0.6 : 1,
                            }}
                            onClick={handleSaveDrugQuote}
                            disabled={isSaveDrugQuoteDisabled}>
                            {isSaveDrugQuoteLoading || isUpdateDrugQuoteLoading ? (
                                <ButtonLoader />
                            ) : editingQuoteId ? (
                                'Update Drug Quote'
                            ) : (
                                'Save Drug Quote'
                            )}
                        </Button>
                    </span>
                    {showSaveQuoteDescriptionTooltip && (
                        <UncontrolledTooltip placement="top" target="save-drug-quote-btn">
                            Quotes description is missing
                        </UncontrolledTooltip>
                    )}
                </div>
            </Col>
        </div>
    );
}

export default DrugQuotes;
